// ✅ Full working ChatBot.jsx with prompt count integration and input blocking

import React, {
 useState,
 useRef,
 useEffect,
 forwardRef,
 useImperativeHandle
} from"react";
import ReactMarkdown from"react-markdown";
import { Prism as SyntaxHighlighter } from"react-syntax-highlighter";
import { oneDark } from"react-syntax-highlighter/dist/esm/styles/prism";
import { useClipboard } from"use-clipboard-copy";
import axios from"axios";
import API_BASE_URL from"../../Config/config";
import PaymentReminderPopup from"./PaymentReminderPopup";
import { checkPaymentStatus } from"../../utils/paymentStatus";

const USER_AVATAR ="👤";
const BOT_AVATAR ="🤖";
const PROMPT_LIMIT = 49;
const POPUP_DELAY_MS = 3 * 60 * 1000; // 3 minutes
const MAX_POPUP_DISPLAYS = 2; // Max 2 times per session

const ChatBot = forwardRef((props, ref) => {
 const [promptsLeft, setPromptsLeft] = useState(PROMPT_LIMIT);
 const [history, setHistory] = useState([]);
 const [question, setQuestion] = useState("");
 const [loading, setLoading] = useState(false);
 const [copiedIdx, setCopiedIdx] = useState(null);
 const [showPaymentPopup, setShowPaymentPopup] = useState(false);
 const [hasPayment, setHasPayment] = useState(false);
 const clipboard = useClipboard();
 const chatBottomRef = useRef(null);
 const popupTimerRef = useRef(null);
 const popupDisplayCountRef = useRef(0);
 const sessionStartTimeRef = useRef(Date.now());

 const userId = JSON.parse(localStorage.getItem("user"))?.userId;

 useEffect(() => {
 const fetchPromptStatus = async () => {
 try {
 const res = await axios.get(
 `${API_BASE_URL}/check-prompt-eligibility/${userId}`
 );
 const parsed = res.data;
 const remaining =
 typeof parsed.remaining ==="number"
 ? parsed.remaining
 : PROMPT_LIMIT;
 setPromptsLeft(remaining);
 } catch (err) {
 console.error("Failed to fetch prompt eligibility:", err);
 }
 };
 fetchPromptStatus();
 }, [userId]);

 // Check payment status and set up popup timer
 useEffect(() => {
 const checkPaymentAndSetupPopup = async () => {
 if (!userId) return;

 try {
 const paymentStatus = await checkPaymentStatus(userId);
 const userHasPayment = paymentStatus.hasPayment && paymentStatus.isValid;
 setHasPayment(userHasPayment);

 // Only show popup if user doesn't have payment
 if (!userHasPayment) {
 // Get login session key - tracks when user logged in
 const loginSessionKey = `login_session_start_${userId}`;
 const popupSessionKey = `chatbot_popup_session_${userId}`;
 
 // Check if this is a new login session (no login timestamp exists)
 let loginStartTime = null;
 const loginSessionData = localStorage.getItem(loginSessionKey);
 
 if (!loginSessionData) {
 // First time accessing ChatBot after login - create login timestamp
 loginStartTime = Date.now();
 localStorage.setItem(loginSessionKey, loginStartTime.toString());
 } else {
 // Existing login session
 loginStartTime = parseInt(loginSessionData, 10);
 if (isNaN(loginStartTime)) {
 // Invalid timestamp, reset
 loginStartTime = Date.now();
 localStorage.setItem(loginSessionKey, loginStartTime.toString());
 }
 }

 // Get popup display tracking
 let popupDisplayCount = 0;
 const popupSessionData = localStorage.getItem(popupSessionKey);
 
 if (popupSessionData) {
 try {
 const popupInfo = JSON.parse(popupSessionData);
 popupDisplayCount = popupInfo.displayCount || 0;
 
 // If login session changed (new login), reset display count
 if (popupInfo.loginStartTime !== loginStartTime) {
 popupDisplayCount = 0;
 }
 } catch (e) {
 // Invalid popup data, reset
 popupDisplayCount = 0;
 }
 }
 
 popupDisplayCountRef.current = popupDisplayCount;
 sessionStartTimeRef.current = loginStartTime;

 // Only show popup if we haven't exceeded max displays
 if (popupDisplayCount < MAX_POPUP_DISPLAYS) {
 // Calculate elapsed time since login
 const elapsedTime = Date.now() - loginStartTime;
 const remainingTime = Math.max(0, POPUP_DELAY_MS - elapsedTime);

 // Only set timer if 3 minutes haven't passed yet
 if (remainingTime > 0) {
 popupTimerRef.current = setTimeout(() => {
 // Check again before showing (payment might have happened)
 checkPaymentStatus(userId).then(status => {
 if (!status.hasPayment || !status.isValid) {
 if (popupDisplayCountRef.current < MAX_POPUP_DISPLAYS) {
 setShowPaymentPopup(true);
 popupDisplayCountRef.current += 1;
 
 // Update popup session info
 const updatedPopupInfo = {
 loginStartTime: loginStartTime,
 displayCount: popupDisplayCountRef.current,
 lastDisplayTime: Date.now()
 };
 localStorage.setItem(popupSessionKey, JSON.stringify(updatedPopupInfo));
 }
 }
 });
 }, remainingTime);
 } else if (elapsedTime >= POPUP_DELAY_MS && popupDisplayCount < MAX_POPUP_DISPLAYS) {
 // 3 minutes already passed, show immediately if haven't exceeded limit
 checkPaymentStatus(userId).then(status => {
 if (!status.hasPayment || !status.isValid) {
 setShowPaymentPopup(true);
 popupDisplayCountRef.current += 1;
 
 const updatedPopupInfo = {
 loginStartTime: loginStartTime,
 displayCount: popupDisplayCountRef.current,
 lastDisplayTime: Date.now()
 };
 localStorage.setItem(popupSessionKey, JSON.stringify(updatedPopupInfo));
 }
 });
 }
 }
 }
 } catch (error) {
 console.error("Error checking payment status for popup:", error);
 }
 };

 checkPaymentAndSetupPopup();

 // Cleanup timer on unmount
 return () => {
 if (popupTimerRef.current) {
 clearTimeout(popupTimerRef.current);
 }
 };
 }, [userId]);

 // Clean up session data when user logs out or payment is made
 useEffect(() => {
 if (hasPayment && userId) {
 // User has payment, clear popup timer
 if (popupTimerRef.current) {
 clearTimeout(popupTimerRef.current);
 popupTimerRef.current = null;
 }
 setShowPaymentPopup(false);
 }
 }, [hasPayment, userId]);

 useImperativeHandle(ref, () => ({
 receiveAnalysis: (externalQuestion, userVisibleMessage) => {
 const msg = userVisibleMessage ||"Get AI feedback for my code.";
 const userMsg = {
 role:"user",
 text: msg,
 time: new Date().toLocaleTimeString()
 };
 setHistory((prev) => [...prev, userMsg]);
 setTimeout(() => {
 handleAsk(externalQuestion, true);
 }, 100);
 }
 }));

 useEffect(() => {
 if (chatBottomRef.current) {
 chatBottomRef.current.scrollIntoView({ behavior:"smooth" });
 }
 }, [history]);

 const handleAsk = async (customQuestion, skipUserMsg) => {
 const actualQ = customQuestion ?? question;
 if (!actualQ.trim() || promptsLeft <= 0) return;

 if (!skipUserMsg) {
 setHistory((prev) => [
 ...prev,
 {
 role:"user",
 text: actualQ,
 time: new Date().toLocaleTimeString()
 }
 ]);
 }

 setLoading(true);
 setQuestion("");

 try {
 const token = localStorage.getItem("token");

 const res = await axios.post(
 `${API_BASE_URL}/api/skillgenbot/ask`,
 { question: actualQ },
 {
 headers: {
"Content-Type":"application/json",
 Authorization: `Bearer ${token}`
 }
 }
 );

 const data = res.data;
 if (data.error) {
 setHistory((prev) => [
 ...prev,
 {
 role:"bot",
 text: `Error: ${data.error}`,
 time: new Date().toLocaleTimeString()
 }
 ]);
 } else {
 // ✅ Step 1: Decrement UI
 setPromptsLeft((prev) => Math.max(prev - 1, 0));

 // ✅ Step 2: Log prompt usage in DB
 await axios.post(`${API_BASE_URL}/increment-prompt-count`, {
 user_id: userId,
 transaction_id:"NA"
 });

 setHistory((prev) => [
 ...prev,
 {
 role:"bot",
 text: data.answer ||"No answer.",
 time: new Date().toLocaleTimeString()
 }
 ]);
 }
 } catch (err) {
 setHistory((prev) => [
 ...prev,
 {
 role:"bot",
 text:"Error: Could not connect to SkillGenBot.",
 time: new Date().toLocaleTimeString()
 }
 ]);
 }

 setLoading(false);
 };

 const handleExport = () => {
 const content = history
 .map(
 (msg) =>
 `[${msg.time}] ${msg.role ==="user" ?"You" :"SkillGenBot"}: ${
 msg.text
 }`
 )
 .join("\n\n---\n\n");
 const blob = new Blob([content], { type:"text/plain" });
 const url = URL.createObjectURL(blob);
 const link = document.createElement("a");
 link.href = url;
 link.download ="skillgenbot_chat_history.txt";
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 URL.revokeObjectURL(url);
 };

 const renderers = {
 code({ node, inline, className, children, ...props }) {
 const match = /language-(\w+)/.exec(className ||"");
 const codeString = String(children).replace(/\n$/,"");
 const idx = node?.position?.start.line || Math.random();

 return !inline && match ? (
 <div style={{ position:"relative" }}>
 <button
 onClick={() => {
 clipboard.copy(codeString);
 setCopiedIdx(idx);
 setTimeout(() => setCopiedIdx(null), 1200);
 }}
 style={{
 position:"absolute",
 right: 6,
 top: 6,
 zIndex: 2,
 background:"linear-gradient(90deg, var(--primary-blue, #4F46E5), #7C3AED)",
 color:"#fff",
 fontWeight: 600,
 border:"none",
 borderRadius: 4,
 fontSize: 13,
 padding:"1px 10px",
 cursor:"pointer",
 boxShadow:"0 2px 8px rgba(37, 99, 235, 0.3)"
 }}
 >
 {copiedIdx === idx ?"Copied!" :"Copy"}
 </button>
 <SyntaxHighlighter
 style={oneDark}
 language={match[1]}
 PreTag="div"
 customStyle={{
 borderRadius: 8,
 background:"#23272f",
 marginTop: 12,
 marginBottom: 6,
 overflowX:"auto",
 whiteSpace:"pre",
 maxWidth:"70vw"
 }}
 {...props}
 >
 {codeString}
 </SyntaxHighlighter>
 </div>
 ) : (
 <code
 style={{
 background:"#0F172A",
 color:"#22D3EE",
 borderRadius: 4,
 padding:"2px 4px",
 border:"1px solid rgba(34, 211, 238, 0.2)"
 }}
 {...props}
 >
 {children}
 </code>
 );
 }
 };

 const handlePopupClose = () => {
 setShowPaymentPopup(false);
 
 // Check if we should show again after a delay (if haven't exceeded limit)
 if (popupDisplayCountRef.current < MAX_POPUP_DISPLAYS && userId) {
 // Show again after 30 seconds if user hasn't paid
 setTimeout(() => {
 checkPaymentStatus(userId).then(status => {
 if ((!status.hasPayment || !status.isValid) && popupDisplayCountRef.current < MAX_POPUP_DISPLAYS) {
 setShowPaymentPopup(true);
 popupDisplayCountRef.current += 1;
 
 const popupSessionKey = `chatbot_popup_session_${userId}`;
 const updatedPopupInfo = {
 loginStartTime: sessionStartTimeRef.current,
 displayCount: popupDisplayCountRef.current,
 lastDisplayTime: Date.now()
 };
 localStorage.setItem(popupSessionKey, JSON.stringify(updatedPopupInfo));
 }
 });
 }, 30000); // 30 seconds delay before showing again
 }
 };

 const handlePopupUpgrade = () => {
 setShowPaymentPopup(false);
 window.location.href ="/subscription-plans";
 };

 return (
 <>
 {/* Payment Reminder Popup */}
 {showPaymentPopup && (
 <PaymentReminderPopup
 onClose={handlePopupClose}
 onUpgrade={handlePopupUpgrade}
 />
 )}

      <div
        style={{
          width: 360,
          height: 600,
          background:"var(--bg-main)",
          borderRadius: 12,
          overflow:"hidden",
          display:"flex",
          flexDirection:"column",
          boxShadow:"0 10px 30px rgba(31, 138, 203, 0.15)",
          border:"1px solid var(--accent-gray)"
        }}
      >
        {/* Header with prompt count and export */}
        <div
          style={{
            background:"var(--primary-blue)",
            color:"#fff",
            padding:"12px 16px",
            fontWeight: 700,
            fontSize: 16,
            display:"flex",
            justifyContent:"space-between",
            alignItems:"center",
            boxShadow:"0 4px 12px rgba(43, 176, 230, 0.2)"
          }}
        >
          <span style={{ display:"flex", alignItems:"center", gap:"6px" }}>
            <span style={{ fontSize: 18 }}>🔷</span> Prompts left: {promptsLeft}
          </span>
          <button
            onClick={handleExport}
            title="Export Chat"
            style={{
              background:"rgba(255, 255, 255, 0.2)",
              color:"#fff",
              border:"1px solid rgba(255, 255, 255, 0.3)",
              padding:"4px 12px",
              borderRadius: 8,
              cursor:"pointer",
              fontSize: 12,
              fontWeight:"bold",
              transition:"all 0.2s"
            }}
            onMouseEnter={(e) => e.target.style.background ="rgba(255, 255, 255, 0.3)"}
            onMouseLeave={(e) => e.target.style.background ="rgba(255, 255, 255, 0.2)"}
          >
            Export
          </button>
        </div>

        {/* Chat messages */}
        <div style={{ flex: 1, padding: 10, overflowY:"auto" }}>
          {history.map((msg, idx) => (
            <div
              key={idx}
              style={{
                display:"flex",
                flexDirection: msg.role ==="user" ?"row-reverse" :"row",
                gap: 10,
                marginBottom: 16
              }}
            >
              <div style={{ fontSize: 28 }}>
                {msg.role ==="user" ? USER_AVATAR : BOT_AVATAR}
              </div>
              <div
                style={{
                  background: msg.role ==="user" ?"var(--primary-blue)" :"#FFFFFF",
                  color: msg.role ==="user" ?"#FFFFFF" :"var(--text-body)",
                  borderRadius:"16px",
                  padding:"12px 16px",
                  maxWidth:"85%",
                  border: msg.role ==="user" ?"none" :"1px solid var(--accent-gray)",
                  boxShadow:"0 2px 8px rgba(0, 0, 0, 0.05)"
                }}
              >
                <div style={{ fontSize: 15, fontWeight: msg.role ==="user" ? 600 : 500 }}>
                  {msg.role ==="bot" ? (
                    <ReactMarkdown components={renderers}>{msg.text}</ReactMarkdown>
                  ) : (
                    msg.text
                  )}
                </div>
              </div>
            </div>
          ))}
          <div ref={chatBottomRef} />
        </div>

        {/* Input area */}
        <div style={{ display:"flex", padding: 10, background:"var(--bg-header-footer)", borderTop:"1px solid var(--accent-gray)" }}>
          <input
            style={{
              flex: 1,
              marginRight: 8,
              borderRadius: 8,
              border:"1px solid var(--accent-gray)",
              padding:"10px 14px",
              fontSize: 15,
              background:"#FFFFFF",
              color:"var(--text-heading)",
              fontWeight: 500,
              outline:"none"
            }}
            type="text"
            placeholder={
              promptsLeft <= 0
                ?"Prompt limit reached. Renew to continue."
                :"Ask SkillGenBot anything..."
            }
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key ==="Enter" && handleAsk()}
            disabled={loading || promptsLeft <= 0}
          />
          <button
            onClick={() => handleAsk()}
            disabled={loading || !question.trim() || promptsLeft <= 0}
            style={{
              borderRadius: 8,
              background:"var(--primary-blue)",
              color:"#fff",
              border:"none",
              fontWeight: 700,
              fontSize: 15,
              padding:"7px 20px",
              cursor: (loading || promptsLeft <= 0) ?"not-allowed" :"pointer",
              transition:"all 0.2s",
              boxShadow:"0 4px 10px rgba(43, 176, 230, 0.3)"
            }}
            onMouseEnter={(e) => {
              if(!loading && promptsLeft > 0) {
                e.target.style.background ="var(--secondary-blue)";
                e.target.style.transform ="translateY(-1px)";
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.background ="var(--primary-blue)";
              e.target.style.transform ="translateY(0)";
            }}
          >
            {loading ?"..." :"Ask"}
          </button>
        </div>
      </div>
 </>
 );
});

export default ChatBot;




